import { useEffect, useState } from 'react';
import type { WorkingPattern } from '@/types/timesheet';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import type { DataMode } from '@/contexts/DataSourceContext';
import { getCachedLiveOrgSettings, getEmployeeWorkingPattern, getOrganisationTimesheetSettings } from '@/services/settingsRepository';

export interface UseTimesheetSettingsDataParams {
  screenMode: DataMode;
  targetEmployeeId: number;
}

export interface UseTimesheetSettingsDataResult {
  organizationSettings: OrganisationTimesheetSettings | undefined;
  employeeWorkingPattern: WorkingPattern | undefined;
}

/**
 * Loads the organisation timesheet settings and the viewed employee's working
 * pattern. In live mode the /employees endpoint does not include the working
 * pattern, so it is fetched separately from the working-patterns API.
 */
export function useTimesheetSettingsData({
  screenMode,
  targetEmployeeId,
}: UseTimesheetSettingsDataParams): UseTimesheetSettingsDataResult {
  // Loaded early (from cache when available) so weekStartDay can drive day order
  const [organizationSettings, setOrganizationSettings] = useState<OrganisationTimesheetSettings | undefined>(() => {
    if (screenMode === 'live') {
      return getCachedLiveOrgSettings() || undefined;
    }
    return undefined;
  });

  const [employeeWorkingPattern, setEmployeeWorkingPattern] = useState<WorkingPattern | undefined>(undefined);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getOrganisationTimesheetSettings(screenMode);
        setOrganizationSettings(settings);
      } catch (error) {
        console.error('Failed to fetch organization settings:', error);
      }
    };
    fetchSettings();
  }, [screenMode]);

  useEffect(() => {
    const fetchEmployeePattern = async () => {
      try {
        const result = await getEmployeeWorkingPattern(targetEmployeeId, screenMode);
        setEmployeeWorkingPattern(result.pattern);
      } catch (error) {
        console.error('Failed to fetch employee working pattern:', error);
      }
    };
    fetchEmployeePattern();
  }, [targetEmployeeId, screenMode]);

  return { organizationSettings, employeeWorkingPattern };
}
